import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { setAccessToken } from '@/lib/api/client';
import { getMeApi } from '@/lib/auth/authApi';
import { safeInternalPath } from '@/lib/url-params';
import { useAuthStore } from '@/stores/auth-store';
import { CircleDot, Loader2 } from 'lucide-react';

export default function OAuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, setChallenge } = useAuthStore();

  useEffect(() => {
    // Error from API redirect (query param) — e.g. registration closed
    const queryError = searchParams.get('error');
    if (queryError) {
      void navigate(`/login?error=${encodeURIComponent(queryError)}`, { replace: true });
      return;
    }

    const hash = window.location.hash.slice(1);
    const params = new URLSearchParams(hash);
    if (params.get('mfa') === '1') {
      const challengeToken = params.get('challengeToken');
      if (!challengeToken) {
        void navigate('/login?error=oauth_failed', { replace: true });
        return;
      }
      // Never leave a challenge token in browser history.
      window.history.replaceState(null, '', window.location.pathname);
      setChallenge({
        token: challengeToken,
        type: params.get('challengeType') ?? 'EMAIL_OTP',
        methodsAllowed: (params.get('methodsAllowed') ?? 'email_otp').split(','),
        obfuscatedEmail: params.get('obfuscatedEmail') ?? '',
      });
      const loginParams = new URLSearchParams({ redirectTo: safeInternalPath(searchParams.get('redirectTo')) });
      if (searchParams.get('fromSource') === 'site') loginParams.set('fromSource', 'site');
      void navigate(`/login?${loginParams}`, { replace: true });
      return;
    }

    const token = params.get('token');
    const isNew = params.get('new') === '1';
    const redirectTo = safeInternalPath(searchParams.get('redirectTo'));

    if (!token) {
      void navigate('/login?error=oauth_failed', { replace: true });
      return;
    }

    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    setAccessToken(token);
    getMeApi()
      .then(user => {
        login(user, token);
        if (isNew) {
          void navigate(ROUTES.OAUTH_ONBOARDING, { replace: true });
        } else {
          void navigate(redirectTo, { replace: true });
        }
      })
      .catch(() => {
        setAccessToken(null);
        void navigate('/login?error=oauth_failed', { replace: true });
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className='min-h-screen bg-gradient-to-br from-emerald-900 to-emerald-500 flex flex-col items-center justify-center gap-4'>
      <CircleDot className='w-10 h-10 text-white' />
      <Loader2 className='w-6 h-6 text-white animate-spin' />
      <p className='text-white/80 text-sm'>Connexion en cours…</p>
    </div>
  );
}
